import { api, mBaseURL } from "@/config/api";
import { unwrapApiError } from "@/lib/error";
import { createURLPath } from "@/lib/utils";
import { APIResponseSuccessSchema } from "@/schema/api";

import { UserSchema, type User } from "@/schema/authentication";
import { CompanyProfile, CompanyProfileSchema, CustomerProfileSchema, type CustomerProfile } from "@/schema/profile";
import { queryOptions } from "@tanstack/react-query";

export const ProfileApiFactory = {
  Endpoint: mBaseURL + "/api/profile",
  Keys: {
    FetchCompanyProfile: () => ["profile", "me", "company"],
    UpdateCompanyProfile: (organization: string) => ["profile", "company", organization],
  },
  Schemas: {
    Response: {
      User: APIResponseSuccessSchema.extend({
        data: UserSchema,
      }),
      Customer: APIResponseSuccessSchema.extend({
        data: CustomerProfileSchema,
      }),
      Company: APIResponseSuccessSchema.extend({
        data: CompanyProfileSchema,
      }),
    },
  },
  Apis: {
    FetchUserProfile: async () => {
      const response = await api(createURLPath(ProfileApiFactory.Endpoint, "me"), {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(ProfileApiFactory.Schemas.Response.User.parse);
    },
    UpdateUserProfile: async (data: Partial<User>) => {
      const response = await api(createURLPath(ProfileApiFactory.Endpoint, "me"), {
        method: "PUT",
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(ProfileApiFactory.Schemas.Response.User.parse);
    },
    FetchCustomerProfile: async () => {
      const response = await api(createURLPath(ProfileApiFactory.Endpoint, "me", "customer"), {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(ProfileApiFactory.Schemas.Response.Customer.parse);
    },
    UpdateCustomerProfile: async (data: Partial<CustomerProfile>) => {
      const response = await api(createURLPath(ProfileApiFactory.Endpoint, "me", "customer"), {
        method: "POST",
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(ProfileApiFactory.Schemas.Response.Customer.parse);
    },
    FetchCompanyProfile: async () => {
      const response = await api(createURLPath(ProfileApiFactory.Endpoint, "me", "company"), {
        method: "GET",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(ProfileApiFactory.Schemas.Response.Company.parse);
    },
    UpdateCompanyProfile: async (data: Partial<CompanyProfile>) => {
      const response = await api(createURLPath(ProfileApiFactory.Endpoint, "me", "company"), {
        method: "POST",
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(ProfileApiFactory.Schemas.Response.Company.parse);
    },
  },
  Queries: {
    FetchCompanyProfile: () => {
      return queryOptions({
        queryKey: ProfileApiFactory.Keys.FetchCompanyProfile(),
        queryFn: ProfileApiFactory.Apis.FetchCompanyProfile,
      });
    },
  },
};
